import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, catchError, map, of } from 'rxjs';

import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';

import {
  SvcActionPlanHistoryApiResponse,
  SvcActionPlanHistoryLog,
  SvcActionPlanHistorySearchResponse,
} from '../models/svc-action-plan-history.model';
import { ISvcActionPlanHistoryRepository } from './svc-action-plan-history.repository.interface';

@Injectable({
  providedIn: 'root',
})
export class SvcActionPlanHistoryHttpRepository implements ISvcActionPlanHistoryRepository {
  readonly #httpClient = inject(HttpClient);
  readonly #appConfig = inject(AppEnvironmentConfig);
  readonly #translocoService = inject(TranslocoService);

  public getHistoryLogs(actionPlanId: number): Observable<SvcActionPlanHistorySearchResponse> {
    const apiUrlActionPlan = this.#appConfig.APIs.apiUrlActionPlan;

    return this.#httpClient
      .get<SvcActionPlanHistoryApiResponse[]>(`${apiUrlActionPlan}/api/actionplan/history/${actionPlanId}`, {
        headers: this.#getEnvironmentHeaders(),
      })
      .pipe(
        map((response) => {
          const items = (response ?? []).map((item) => this.#mapToHistoryLog(item));
          return { items, totalCount: items.length };
        }),
        catchError(() => of({ items: [], totalCount: 0 }))
      );
  }

  #mapToHistoryLog(item: SvcActionPlanHistoryApiResponse): SvcActionPlanHistoryLog {
    return {
      id: item.historyId,
      actionPlanId: item.actionPlanUniqueId,
      description: this.#normalizeEventText(item.eventText),
      userName: '',
      date: item.historyDate,
      actionType: item.iconKey,
      details: this.#normalizeComment(item.comment),
    };
  }

  #normalizeEventText(eventText?: string | null): string {
    const normalizedText = String(eventText ?? '').trim();
    if (!normalizedText) return '';

    const translatedText = this.#translocoService.translate(normalizedText);
    return String(translatedText ?? normalizedText).trim() || normalizedText;
  }

  #normalizeComment(comment?: string | null): string | undefined {
    const normalizedComment = String(comment ?? '').trim();
    if (!normalizedComment) return undefined;

    const separatorIndex = normalizedComment.indexOf(':');
    if (separatorIndex <= 0) return normalizedComment;

    const prefix = normalizedComment.slice(0, separatorIndex).trim().toLowerCase();
    if (prefix === 'comment' || prefix === 'comentario' || prefix.startsWith('coment')) {
      return normalizedComment.slice(separatorIndex + 1).trim();
    }

    return normalizedComment;
  }

  #getEnvironmentHeaders(): { EnvironmentUrl: string } {
    const fromStorage = typeof localStorage !== 'undefined' ? localStorage.getItem('environmentUrl') : null;
    const fromWindow = typeof window !== 'undefined' ? window.location.origin : '';
    return { EnvironmentUrl: fromStorage || fromWindow };
  }
}
